package ai.jessie.web.work

import ai.jessie.shared.work.WorkCalendarResponse
import ai.jessie.shared.work.WorkCalendarView
import ai.jessie.web.onboarding.onboardingAlert
import ai.jessie.web.theme.ButtonSize
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.CalendarArrowStyle
import ai.jessie.web.theme.CalendarSummaryStyle
import ai.jessie.web.theme.CalendarToolbarStyle
import ai.jessie.web.theme.CalendarToolbarTitleStyle
import ai.jessie.web.theme.CardStyle
import ai.jessie.web.theme.RowStyle
import ai.jessie.web.theme.SegmentStyle
import ai.jessie.web.theme.SegmentedStyle
import ai.jessie.web.theme.SmallStyle
import ai.jessie.web.theme.StatusStyle
import ai.jessie.web.theme.VisuallyHiddenStyle
import ai.jessie.web.theme.disclosure
import ai.jessie.web.theme.linkButton
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.silk.style.toAttrs
import kotlin.js.Date
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Nav
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

@Composable
fun workCalendar(
    route: WorkCalendarRoute,
    state: WorkCalendarUiState,
    actions: WorkCalendarActions,
    editor: WorkState?,
) {
    var selectedDate by remember { mutableStateOf<String?>(null) }
    var trigger by remember { mutableStateOf<HTMLElement?>(null) }
    DisposableEffect(editor) {
        if (editor != null) trigger = document.activeElement as? HTMLElement
        onDispose {}
    }
    LaunchedEffect(editor, state.busy) {
        if (editor == null && !state.busy && state.error == null) {
            trigger?.takeIf { it.isConnected }?.focus()
            trigger = null
        }
    }
    LaunchedEffect(editor?.state?.draft?.workDate) {
        editor?.state?.draft?.workDate?.takeIf(::validCalendarDate)?.let { selectedDate = it }
    }
    val calendarActions =
        actions.copy(
            createOn = {
                selectedDate = it
                actions.createOn(it)
            },
            createFromSelection = {
                selectedDate = state.selection.date
                actions.createFromSelection()
            },
        )
    if (editor?.state?.error == null || editor.state.draft != null)
        calendarRange(route, state, calendarActions, selectedDate) { selectedDate = it }
    if (editor != null) calendarEditor(editor.state, editor.actions, actions)
    P { A(href = "/organisations/${route.organisationId}") { Text("Zurück zur Organisation") } }
}

@Composable
private fun calendarRange(
    route: WorkCalendarRoute,
    state: WorkCalendarUiState,
    actions: WorkCalendarActions,
    selectedDate: String?,
    selectDate: (String) -> Unit,
) {
    onboardingAlert(state.error, state.busy)
    if (state.error?.authenticationRequired == true) return
    if (state.busy)
        P(attrs = StatusStyle.toAttrs { attr("role", "status") }) {
            Text("Kalender wird geladen …")
        }
    state.notice?.let { P(attrs = StatusStyle.toAttrs { attr("role", "status") }) { Text(it) } }
    Div(attrs = CardStyle.toAttrs()) {
        calendarNavigation(route, state.calendar)
        state.calendar?.let { calendar ->
            calendarSummary(calendar)
            if (calendar.view == WorkCalendarView.MONTH)
                calendarMonth(calendar, state, actions, selectDate)
            else calendarWeek(route, calendar, state)
        }
    }
    state.calendar?.let { calendar ->
        if (calendar.view == WorkCalendarView.WEEK)
            disclosure("Zeitraum erfassen") {
                calendarSelectionForm(calendar, state, actions)
            }
        val date =
            selectedDate ?: state.selection.date.takeIf { it.isNotEmpty() } ?: calendar.currentDate
        calendar.days
            .firstOrNull { it.date == date }
            ?.let { day -> calendarEntries(route, day, calendar, state, actions) }
    }
    Div(attrs = RowStyle.toAttrs()) {
        themedButton(
            "Kalender erneut laden",
            actions.refresh,
            variant = ButtonVariant.GHOST,
            size = ButtonSize.SMALL,
            disabled = state.busy,
        )
    }
}

// Navigation keeps the server's range anchors and the existing view/date query values.
@Composable
private fun calendarNavigation(route: WorkCalendarRoute, calendar: WorkCalendarResponse?) {
    val base = "/organisations/${route.organisationId}/calendar"
    val anchor = route.date?.let { "&date=$it" }.orEmpty()
    val active = calendar?.view ?: route.view ?: WorkCalendarView.WEEK
    Nav(attrs = CalendarToolbarStyle.toAttrs { attr("aria-label", "Kalenderzeitraum") }) {
        H2(attrs = CalendarToolbarTitleStyle.toAttrs()) {
            Text(calendar?.let(::calendarRangeTitle) ?: "Kalender")
        }
        Div(attrs = RowStyle.toAttrs()) {
            linkButton("Heute", "$base?view=${active.name}")
            Div(attrs = SegmentedStyle.toAttrs()) {
                listOf(WorkCalendarView.MONTH to "Monat", WorkCalendarView.WEEK to "Woche")
                    .forEach { (view, label) ->
                        A(
                            href = "$base?view=${view.name}$anchor",
                            attrs =
                                SegmentStyle.toAttrs {
                                    attr("aria-pressed", (active == view).toString())
                                    if (active == view) attr("aria-current", "page")
                                },
                        ) {
                            Text(label)
                        }
                    }
            }
            if (calendar != null) {
                val period = if (calendar.view == WorkCalendarView.WEEK) "Woche" else "Monat"
                val view = "$base?view=${calendar.view.name}"
                calendarArrow(
                    "Vorherige $period",
                    "$view&date=${calendar.previousDate}",
                    "‹",
                )
                calendarArrow("Nächste $period", "$view&date=${calendar.nextDate}", "›")
            }
        }
    }
}

@Composable
private fun calendarArrow(label: String, href: String, glyph: String) {
    // shortcut: text chevrons stand in for the icon, use icon(glyph) once the Font Awesome kit (B1)
    // is licensed.
    A(href = href, attrs = CalendarArrowStyle.toAttrs { attr("aria-label", label) }) {
        Span(attrs = { attr("aria-hidden", "true") }) { Text(glyph) }
    }
}

@Composable
private fun calendarSummary(calendar: WorkCalendarResponse) {
    val period = if (calendar.view == WorkCalendarView.WEEK) "Woche" else "Monat"
    Div(attrs = CalendarSummaryStyle.toAttrs()) {
        // The toolbar title already names the range; screen readers keep the exact dates.
        P(attrs = VisuallyHiddenStyle.toAttrs { id("calendar-range") }) {
            Text(
                "$period ${calendar.rangeStart} bis ${calendar.rangeEnd} · Zeitzone ${calendar.timeZone}"
            )
        }
        P(attrs = VisuallyHiddenStyle.toAttrs { id("calendar-total") }) {
            Text("Summe deiner eigenen Arbeitseinträge: ${minutesLabel(calendar.totalMinutes)}")
        }
        P(attrs = VisuallyHiddenStyle.toAttrs()) {
            Text("Der Kalender zeigt nur deine eigene Arbeit in dieser Organisation.")
        }
        if (calendar.view == WorkCalendarView.WEEK)
            P(attrs = SmallStyle.toAttrs()) { Text("Ganztägig · Einträge ohne Startzeit") }
        if (calendar.days.all { it.entries.isEmpty() })
            P { Text("Keine eigenen Arbeitseinträge in diesem Zeitraum.") }
    }
}

internal fun calendarDateLabel(date: String): String {
    val parts = date.split("-")
    return "${parts[2].toInt()}. ${calendarMonthName(date)} ${parts[0]}"
}

internal fun calendarMonthName(date: String) = MONTHS[Date("${date}T00:00:00Z").getUTCMonth()]

internal fun calendarWeekday(date: String) = WEEKDAY_NAMES[Date("${date}T00:00:00Z").getUTCDay()]

internal fun calendarHours(minutes: Int): String =
    (minutes / MINUTES_PER_HOUR).asDynamic().toFixed(2) as String

internal fun calendarRangeTitle(calendar: WorkCalendarResponse): String =
    if (calendar.view == WorkCalendarView.MONTH)
        "${calendarMonthName(calendar.rangeStart)} ${calendar.rangeStart.substringBefore("-")}"
    else "${calendarDateLabel(calendar.rangeStart)} – ${calendarDateLabel(calendar.rangeEnd)}"

private val MONTHS =
    listOf(
        "Januar",
        "Februar",
        "März",
        "April",
        "Mai",
        "Juni",
        "Juli",
        "August",
        "September",
        "Oktober",
        "November",
        "Dezember",
    )
private val WEEKDAY_NAMES =
    listOf("Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag")

// A label for a server-supplied date; UTC on both sides, so no browser time zone takes part.
internal fun weekdayLabel(date: String): String {
    val day = Date("${date}T00:00:00Z").getUTCDay()
    return WEEKDAYS.getOrElse(day) { "" }
}

private val WEEKDAYS = listOf("So", "Mo", "Di", "Mi", "Do", "Fr", "Sa")

private const val MINUTES_PER_HOUR = 60.0
